/**
 * IndexedDB Database for Offline Caching
 *
 * Stores shipment data for offline access by drivers
 * - Permanent drivers: Individual shipments and grouped shipments
 * - Temporary drivers: Public token shipments
 */

import {
  Country,
  PublicShipmentForDrivers,
  ShipmentGroupForDrivers,
  Comment,
} from "../types";
import { Service } from "../types/service";

const DB_NAME = "livecargo-offline";
const DB_VERSION = 7;

// Object store names
export const STORES = {
  SHIPMENTS: "shipments", // Individual shipments for permanent drivers
  GROUPS: "groups", // Shipment groups for permanent drivers
  PUBLIC_SHIPMENTS: "publicShipments", // Public token shipments for temporary drivers
  COUNTRIES: "countries", // Cached countries list for driver UI
  SERVICES: "services", // Cached services list for driver UI
  COMMENTS: "comments", // Cached comments per shipment/token
  METADATA: "metadata", // Cache metadata (timestamps, versions)
} as const;

// Cache metadata keys
export const METADATA_KEYS = {
  LAST_SYNC: "lastSync",
  CACHE_VERSION: "cacheVersion",
  USER_ID: "userId",
} as const;

export interface CachedShipment extends PublicShipmentForDrivers {
  _cached_at: number;
  _cache_key: string;
}

export interface CachedGroup extends ShipmentGroupForDrivers {
  _cached_at: number;
  _cache_key: string;
}

export interface CacheMetadata {
  key: string;
  value: any;
  timestamp: number;
}

export interface CachedServicesRecord {
  _cache_key: string;
  _cached_at: number;
  services: Service[];
}

export interface CachedCommentsRecord {
  key: string; // e.g. driver_public_<token>
  comments: Comment[];
  timestamp: number;
}

export interface CachedCountry extends Country {
  _cached_at: number;
  _cache_key: string;
}

/**
 * Open IndexedDB connection
 */
export const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("[IndexedDB] Failed to open database:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const oldVersion = event.oldVersion;

      // console.log("[IndexedDB] Upgrading database schema...");

      // Create object stores
      if (!db.objectStoreNames.contains(STORES.SHIPMENTS)) {
        const shipmentStore = db.createObjectStore(STORES.SHIPMENTS, {
          keyPath: "_cache_key",
        });
        shipmentStore.createIndex("shipmentNumber", "shipmentNumber", {
          unique: false,
        });
        shipmentStore.createIndex("status", "status", { unique: false });
        shipmentStore.createIndex("_cached_at", "_cached_at", {
          unique: false,
        });
        // console.log("[IndexedDB] Created shipments store");
      } else if (oldVersion < 7) {
        // Migration: Delete the redundant _cache_key index if upgrading from old version
        const transaction = (event.target as IDBOpenDBRequest).transaction!;
        const shipmentStore = transaction.objectStore(STORES.SHIPMENTS);

        if (shipmentStore.indexNames.contains("_cache_key")) {
          shipmentStore.deleteIndex("_cache_key");
          // console.log("[IndexedDB] Removed redundant _cache_key index");
        }
      }

      if (!db.objectStoreNames.contains(STORES.GROUPS)) {
        const groupStore = db.createObjectStore(STORES.GROUPS, {
          keyPath: "_id",
        });
        groupStore.createIndex("groupNumber", "groupNumber", { unique: false });
        groupStore.createIndex("_cached_at", "_cached_at", { unique: false });
        groupStore.createIndex("_cache_key", "_cache_key", { unique: false });
        // console.log("[IndexedDB] Created groups store");
      }

      if (!db.objectStoreNames.contains(STORES.PUBLIC_SHIPMENTS)) {
        const publicStore = db.createObjectStore(STORES.PUBLIC_SHIPMENTS, {
          keyPath: "_cache_key",
        });
        publicStore.createIndex("shipmentNumber", "shipmentNumber", {
          unique: false,
        });
        publicStore.createIndex("_cached_at", "_cached_at", { unique: false });
        // console.log("[IndexedDB] Created public shipments store");
      }

      if (!db.objectStoreNames.contains(STORES.COUNTRIES)) {
        const countryStore = db.createObjectStore(STORES.COUNTRIES, {
          keyPath: "_cache_key",
        });
        countryStore.createIndex("_cached_at", "_cached_at", { unique: false });
        // console.log("[IndexedDB] Created countries store");
      }

      if (!db.objectStoreNames.contains(STORES.SERVICES)) {
        const serviceStore = db.createObjectStore(STORES.SERVICES, {
          keyPath: "_cache_key",
        });
        serviceStore.createIndex("_cached_at", "_cached_at", { unique: false });
        // console.log("[IndexedDB] Created services store");
      }

      if (!db.objectStoreNames.contains(STORES.COMMENTS)) {
        const commentStore = db.createObjectStore(STORES.COMMENTS, {
          keyPath: "key",
        });
        commentStore.createIndex("timestamp", "timestamp", { unique: false });
        // console.log("[IndexedDB] Created comments store");
      }

      if (!db.objectStoreNames.contains(STORES.METADATA)) {
        const metaStore = db.createObjectStore(STORES.METADATA, {
          keyPath: "key",
        });
        metaStore.createIndex("timestamp", "timestamp", { unique: false });
        // console.log("[IndexedDB] Created metadata store");
      }
    };
  });
};

/**
 * Cache countries list (single record)
 */
export const cacheCountries = async (countries: Country[]): Promise<void> => {
  const db = await openDB();
  const record = {
    _cache_key: "countries",
    _cached_at: Date.now(),
    countries,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COUNTRIES], "readwrite");
    const store = transaction.objectStore(STORES.COUNTRIES);
    const request = store.put(record);

    request.onsuccess = () => {
      // console.log("[IndexedDB] Cached countries:", countries.length);
      resolve();
    };

    request.onerror = () => {
      console.error("[IndexedDB] Failed to cache countries:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Get cached countries list
 */
export const getCachedCountries = async (): Promise<Country[] | null> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COUNTRIES], "readonly");
    const store = transaction.objectStore(STORES.COUNTRIES);
    const request = store.get("countries");

    request.onsuccess = () => {
      resolve(request.result?.countries || null);
    };

    request.onerror = () => {
      console.error(
        "[IndexedDB] Failed to get cached countries:",
        request.error,
      );
      reject(request.error);
    };
  });
};

/**
 * Cache services list (single record)
 */
export const cacheServices = async (services: Service[]): Promise<void> => {
  const db = await openDB();
  const record: CachedServicesRecord = {
    _cache_key: "services",
    _cached_at: Date.now(),
    services,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SERVICES], "readwrite");
    const store = transaction.objectStore(STORES.SERVICES);
    const request = store.put(record);

    request.onsuccess = () => {
      // console.log("[IndexedDB] Cached services:", services.length);
      resolve();
    };

    request.onerror = () => {
      console.error("[IndexedDB] Failed to cache services:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Cache driver comments for a public token (shipment-drivers-view)
 */
export const cacheDriverComments = async (
  token: string,
  comments: Comment[],
): Promise<void> => {
  const db = await openDB();
  const record: CachedCommentsRecord = {
    key: `driver_public_${token}`,
    comments,
    timestamp: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COMMENTS], "readwrite");
    const store = transaction.objectStore(STORES.COMMENTS);
    const request = store.put(record);

    request.onsuccess = () => {
      // console.log(
      //   "[IndexedDB] Cached driver comments for token:",
      //   token,
      //   "count:",
      //   comments.length,
      // );
      resolve();
    };

    request.onerror = () => {
      console.error(
        "[IndexedDB] Failed to cache driver comments:",
        request.error,
      );
      reject(request.error);
    };
  });
};

/**
 * Get cached driver comments for a public token
 */
export const getCachedDriverComments = async (
  token: string,
): Promise<Comment[] | null> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COMMENTS], "readonly");
    const store = transaction.objectStore(STORES.COMMENTS);
    const request = store.get(`driver_public_${token}`);

    request.onsuccess = () => {
      resolve(request.result?.comments || null);
    };

    request.onerror = () => {
      console.error(
        "[IndexedDB] Failed to get cached driver comments:",
        request.error,
      );
      reject(request.error);
    };
  });
};

/**
 * Cache protected driver comments for a shipment ID (permanent drivers)
 */
export const cacheProtectedDriverComments = async (
  shipmentId: string,
  comments: Comment[],
): Promise<void> => {
  const db = await openDB();
  const record: CachedCommentsRecord = {
    key: `driver_protected_${shipmentId}`,
    comments,
    timestamp: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COMMENTS], "readwrite");
    const store = transaction.objectStore(STORES.COMMENTS);
    const request = store.put(record);

    request.onsuccess = () => {
      // console.log(
      //   "[IndexedDB] Cached protected driver comments for shipment:",
      //   shipmentId,
      //   "count:",
      //   comments.length,
      // );
      resolve();
    };

    request.onerror = () => {
      console.error(
        "[IndexedDB] Failed to cache protected driver comments:",
        request.error,
      );
      reject(request.error);
    };
  });
};

/**
 * Get cached protected driver comments for a shipment ID
 */
export const getCachedProtectedDriverComments = async (
  shipmentId: string,
): Promise<Comment[] | null> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.COMMENTS], "readonly");
    const store = transaction.objectStore(STORES.COMMENTS);
    const request = store.get(`driver_protected_${shipmentId}`);

    request.onsuccess = () => {
      resolve(request.result?.comments || null);
    };

    request.onerror = () => {
      console.error(
        "[IndexedDB] Failed to get cached protected driver comments:",
        request.error,
      );
      reject(request.error);
    };
  });
};

/**
 * Get cached services list
 */
export const getCachedServices = async (): Promise<Service[] | null> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SERVICES], "readonly");
    const store = transaction.objectStore(STORES.SERVICES);
    const request = store.get("services");

    request.onsuccess = () => {
      resolve(request.result?.services || null);
    };

    request.onerror = () => {
      console.error(
        "[IndexedDB] Failed to get cached services:",
        request.error,
      );
      reject(request.error);
    };
  });
};

/**
 * Cache a shipment (permanent driver)
 */
export const cacheShipment = async (
  token: string,
  shipment: PublicShipmentForDrivers,
): Promise<void> => {
  const db = await openDB();
  // console.log(shipment);

  const cachedShipment: CachedShipment = {
    ...shipment,
    _cached_at: Date.now(),
    _cache_key: `shipment_${token}`,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SHIPMENTS], "readwrite");
    const store = transaction.objectStore(STORES.SHIPMENTS);
    const request = store.put(cachedShipment);

    request.onsuccess = () => {
      // console.log("[IndexedDB] Cached shipment:", shipment.shipmentNumber);
      resolve();
    };

    request.onerror = () => {
      console.error("[IndexedDB] Failed to cache shipment:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Get cached shipment (permanent driver)
 */
export const getCachedShipment = async (
  token: string,
): Promise<CachedShipment | null> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SHIPMENTS], "readonly");
    const store = transaction.objectStore(STORES.SHIPMENTS);
    const request = store.get(`shipment_${token}`);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      console.error("[IndexedDB] Failed to get shipment:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Cache multiple shipments (from shipment list)
 */
export const cacheShipments = async (
  shipments: PublicShipmentForDrivers[],
): Promise<void> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SHIPMENTS], "readwrite");
    const store = transaction.objectStore(STORES.SHIPMENTS);

    shipments.forEach((shipment) => {
      const cachedShipment: CachedShipment = {
        ...shipment,
        _cached_at: Date.now(),
        _cache_key: `shipment_${shipment._id}`,
      };
      store.put(cachedShipment);
    });

    transaction.oncomplete = () => {
      // console.log("[IndexedDB] Cached shipments:", shipments.length);
      resolve();
    };

    transaction.onerror = () => {
      console.error(
        "[IndexedDB] Failed to cache shipments:",
        transaction.error,
      );
      reject(transaction.error);
    };
  });
};

/**
 * Get all cached shipments (for offline list view)
 */
export const getAllCachedShipments = async (): Promise<CachedShipment[]> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.SHIPMENTS], "readonly");
    const store = transaction.objectStore(STORES.SHIPMENTS);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = () => {
      console.error("[IndexedDB] Failed to get all shipments:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Cache a shipment group (permanent driver)
 */
export const cacheGroup = async (
  group: ShipmentGroupForDrivers,
): Promise<void> => {
  const db = await openDB();

  const cachedGroup: CachedGroup = {
    ...group,
    _cached_at: Date.now(),
    _cache_key: `group_${group._id}`,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.GROUPS], "readwrite");
    const store = transaction.objectStore(STORES.GROUPS);
    const request = store.put(cachedGroup);

    request.onsuccess = () => {
      // console.log("[IndexedDB] Cached group:", group.groupNumber);
      resolve();
    };

    request.onerror = () => {
      console.error("[IndexedDB] Failed to cache group:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Get cached group
 */
export const getCachedGroup = async (
  groupId: string,
): Promise<CachedGroup | null> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.GROUPS], "readonly");
    const store = transaction.objectStore(STORES.GROUPS);
    const request = store.get(groupId);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      console.error("[IndexedDB] Failed to get group:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Cache multiple groups
 */
export const cacheGroups = async (
  groups: ShipmentGroupForDrivers[],
): Promise<void> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.GROUPS], "readwrite");
    const store = transaction.objectStore(STORES.GROUPS);

    groups.forEach((group) => {
      const cachedGroup: CachedGroup = {
        ...group,
        _cached_at: Date.now(),
        _cache_key: `group_${group._id}`,
      };
      store.put(cachedGroup);
    });

    transaction.oncomplete = () => {
      // console.log("[IndexedDB] Cached groups:", groups.length);
      resolve();
    };

    transaction.onerror = () => {
      console.error("[IndexedDB] Failed to cache groups:", transaction.error);
      reject(transaction.error);
    };
  });
};

/**
 * Get all cached groups
 */
export const getAllCachedGroups = async (): Promise<CachedGroup[]> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.GROUPS], "readonly");
    const store = transaction.objectStore(STORES.GROUPS);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = () => {
      console.error("[IndexedDB] Failed to get all groups:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Cache public shipment (temporary driver)
 */
export const cachePublicShipment = async (
  token: string,
  shipment: PublicShipmentForDrivers,
): Promise<void> => {
  const db = await openDB();

  const cachedShipment: CachedShipment = {
    ...shipment,
    _cached_at: Date.now(),
    _cache_key: `public_${token}`,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PUBLIC_SHIPMENTS], "readwrite");
    const store = transaction.objectStore(STORES.PUBLIC_SHIPMENTS);
    const request = store.put(cachedShipment);

    request.onsuccess = () => {
      // console.log("[IndexedDB] Cached public shipment:", token);
      resolve();
    };

    request.onerror = () => {
      console.error(
        "[IndexedDB] Failed to cache public shipment:",
        request.error,
      );
      reject(request.error);
    };
  });
};

/**
 * Get cached public shipment (temporary driver)
 */
export const getCachedPublicShipment = async (
  token: string,
): Promise<CachedShipment | null> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PUBLIC_SHIPMENTS], "readonly");
    const store = transaction.objectStore(STORES.PUBLIC_SHIPMENTS);
    const request = store.get(`public_${token}`);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      console.error(
        "[IndexedDB] Failed to get public shipment:",
        request.error,
      );
      reject(request.error);
    };
  });
};

/**
 * Set metadata
 */
export const setMetadata = async (key: string, value: any): Promise<void> => {
  const db = await openDB();

  const metadata: CacheMetadata = {
    key,
    value,
    timestamp: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.METADATA], "readwrite");
    const store = transaction.objectStore(STORES.METADATA);
    const request = store.put(metadata);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get metadata
 */
export const getMetadata = async (key: string): Promise<any | null> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.METADATA], "readonly");
    const store = transaction.objectStore(STORES.METADATA);
    const request = store.get(key);

    request.onsuccess = () => {
      resolve(request.result?.value || null);
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all cached data (useful for logout or testing)
 */
export const clearAllCache = async (): Promise<void> => {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      [
        STORES.SHIPMENTS,
        STORES.GROUPS,
        STORES.PUBLIC_SHIPMENTS,
        STORES.METADATA,
      ],
      "readwrite",
    );

    transaction.objectStore(STORES.SHIPMENTS).clear();
    transaction.objectStore(STORES.GROUPS).clear();
    transaction.objectStore(STORES.PUBLIC_SHIPMENTS).clear();
    transaction.objectStore(STORES.METADATA).clear();

    transaction.oncomplete = () => {
      // console.log("[IndexedDB] All cache cleared");
      resolve();
    };

    transaction.onerror = () => {
      console.error("[IndexedDB] Failed to clear cache:", transaction.error);
      reject(transaction.error);
    };
  });
};

/**
 * Check if shipment is stale (older than 24 hours)
 */
export const isStale = (
  cachedAt: number,
  maxAgeHours: number = 24,
): boolean => {
  const now = Date.now();
  const age = now - cachedAt;
  const maxAge = maxAgeHours * 60 * 60 * 1000; // Convert hours to ms
  return age > maxAge;
};

/**
 * Get cache statistics
 */
export const getCacheStats = async (): Promise<{
  shipments: number;
  groups: number;
  publicShipments: number;
  totalSize: number;
}> => {
  // const db = await openDB();

  return new Promise((resolve, reject) => {
    const shipments = getAllCachedShipments();
    const groups = getAllCachedGroups();

    Promise.all([shipments, groups])
      .then(([s, g]) => {
        resolve({
          shipments: s.length,
          groups: g.length,
          publicShipments: 0, // Can add if needed
          totalSize: 0, // Browser doesn't provide easy way to get exact size
        });
      })
      .catch(reject);
  });
};
